/**
 * EntityInfo component for the React application.
 * Displays information about a shelter or entity, including hours of operation, meals, and bed availability.
 *
 * Author: Jennifer Tran, Meng Li
 * Created on: 2024-11-01
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import '../../assets/css/EntityInfo.css';
import { ImFacebook2 } from "react-icons/im";
import { FaInstagramSquare, FaTwitterSquare, FaYoutubeSquare, FaExternalLinkAlt } from "react-icons/fa";

const EntityInfo = () => {
    const [entity, setEntity] = useState(null);
    const location = useLocation();

    // Fetch entity data from Firebase based on the entity ID in the URL query parameters
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const entityId = searchParams.get('entityId');

        const fetchEntityData = async () => {
            if (!entityId) return;
            try {
                const docRef = doc(db, 'entities', entityId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setEntity(docSnap.data());
                } else {
                    console.warn(`No data found for entity_id ${entityId}.`);
                }
            } catch (error) {
                console.error("Error fetching data from Firebase:", error);
            }
        };

        fetchEntityData();
    }, [location.search]);

    return (
        <div className="entityInfo-page">
            <div className="square-section-entity entity-info-section">
                {entity ? (
                    <>
                        <h1 className="entity-name">{entity.entity_name}</h1>
                        <div className="links-row">
                            {entity.official_website && (
                                <a className="official-link" href={entity.official_website} target="_blank" rel="noopener noreferrer">
                                    Official Website <FaExternalLinkAlt />
                                </a>
                            )}
                            <div className="social-links">
                                {entity.social_media?.facebook && (
                                    <a href={entity.social_media.facebook} target="_blank" rel="noopener noreferrer">
                                        <ImFacebook2 />
                                    </a>
                                )}
                                {entity.social_media?.instagram && (
                                    <a href={entity.social_media.instagram} target="_blank" rel="noopener noreferrer">
                                        <FaInstagramSquare />
                                    </a>
                                )}
                                {entity.social_media?.twitter && (
                                    <a href={entity.social_media.twitter} target="_blank" rel="noopener noreferrer">
                                        <FaTwitterSquare />
                                    </a>
                                )}
                                {entity.social_media?.youtube && (
                                    <a href={entity.social_media.youtube} target="_blank" rel="noopener noreferrer">
                                        <FaYoutubeSquare />
                                    </a>
                                )}
                            </div>
                        </div>
                        <p className="address">
                            {entity.address?.address_line_1}
                            {entity.address?.address_line_2 && `, ${entity.address.address_line_2}`}
                            {entity.address?.city && `, ${entity.address.city}`}
                            {entity.address?.province && `, ${entity.address.province}`}
                            {entity.address?.country && `, ${entity.address.country}`}
                            {entity.address?.postal_code && ` ${entity.address.postal_code}`}
                        </p>
                    </>
                ) : (
                    <p>Loading...</p>
                )}
            </div>

            {entity?.services?.map((service, index) => (
                <div key={index} className="service-section">
                    <div className="service-header">
                        {service.type}{service.sub_title && `, ${service.sub_title}`}
                    </div>
                    
                    <div className="service-details">
                        {service.hours_of_operation && (
                            <div className="detail-section">
                                <p className="label">Hours of Operation:</p>
                                {service.hours_of_operation.map((hour, idx) => (
                                    <p key={idx} className="detail-line">
                                        {hour.description ? `${hour.description}: ` : ''}{hour.hours}
                                    </p>
                                ))}
                            </div>
                        )}

                        {service.phone_numbers && (
                            <div className="detail-section">
                                <p className="label">Phone Numbers:</p>
                                {service.phone_numbers.map((phone, idx) => (
                                    <p key={idx} className="detail-line">
                                        {phone.description}: <a href={`tel:${phone.number}`}>{phone.number}</a>
                                    </p>
                                ))}
                            </div>
                        )}

                        {service.email && (
                            <div className="detail-section">
                                <p className="label">Email:</p>
                                {service.email.map((email, idx) => (
                                    <p key={idx} className="detail-line">
                                        {email.description}: <a href={`mailto:${email.email_address}`}>{email.email_address}</a>
                                    </p>
                                ))}
                            </div>
                        )}

                        {service['211_ontario_website'] && (
                            <div className="detail-section">
                                <p className="label">211 Ontario Website:</p>
                                <p className="detail-line">
                                    <a href={service['211_ontario_website']} target="_blank" rel="noopener noreferrer" className="underline">
                                        {service['211_ontario_website']}
                                    </a>
                                </p>
                            </div>
                        )}

                        {service.languages && (
                            <div className="detail-section">
                                <p className="label">Languages:</p>
                                <p className="detail-line">{service.languages.join(', ')}</p>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default EntityInfo;